import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { store } from "@app/store";

import { HTTP_STATUS_CODE } from "@constants/api.response.code";

import { setApiError, setRateLimitExpiry } from "@common/ApiSlice";

import { getRateLimitExpiry, waitForRateLimitCooldown } from "@api/rateLimit";

const getProjectInfoHeaders = () => {
  const { projectData, activeCase } = store?.getState()?.projectInfo ?? {};
  return {
    projectName: encodeURIComponent(projectData?.projectInfoVM?.projectName ?? ""),
    caseName: encodeURIComponent(activeCase?.caseName ?? ""),
  };
};

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_PATH,
  prepareHeaders: (headers, { getState }) => {
    const idToken = getState().Auth?.idToken;
    if (idToken) {
      headers.set("Authorization", `Bearer ${idToken}`);
      const { projectName, caseName } = getProjectInfoHeaders();
      headers.set("x-user-proj-name", projectName);
      headers.set("x-user-case-name", caseName);
    }
    return headers;
  },
});

const baseQueryWithInterceptor = async (args, api, extraOptions) => {
  let result;
  let isRetryPending = true;

  while (isRetryPending) {
    const rateLimitExpiry = api.getState().apiData.rateLimitExpiry;
    if (rateLimitExpiry > Date.now()) {
      await waitForRateLimitCooldown(rateLimitExpiry);
    }

    result = await baseQuery(args, api, extraOptions);

    if (result.error?.status === HTTP_STATUS_CODE.TOO_MANY_REQUESTS) {
      const expiry = getRateLimitExpiry(result.meta?.response?.headers, result.error?.data);
      store.dispatch(setRateLimitExpiry(expiry));
      await waitForRateLimitCooldown(expiry);
      continue;
    }

    isRetryPending = false;
  }

  if (result.error) {
    const { error } = result;
    error.type = "ApiError";
    const { status, data } = error;
    store.dispatch(setApiError({ status, data }));
    throw error;
  }

  return result;
};

const headers = { "Content-type": "application/json; charset=UTF-8" };

// Utility function to simplify mutation configurations
const createMutation = method => data => {
  const { Method, ...payload } = data;
  return {
    method,
    url: `/${Method}`,
    headers,
    body: payload,
  };
};

export const postApi = createApi({
  reducerPath: "postApi",
  baseQuery: baseQueryWithInterceptor,
  endpoints: builder => ({
    getAllData: builder.query({
      query: urlname => ({
        url: urlname,
        method: "GET",
      }),
    }),
    deleteData: builder.mutation({ query: createMutation("DELETE") }),
    deleteDataRecord: builder.mutation({ query: createMutation("PUT") }),
    createData: builder.mutation({ query: createMutation("POST") }),
    updateData: builder.mutation({ query: createMutation("PUT") }),
    calcEngineData: builder.mutation({ query: createMutation("POST") }),
    chemicalAdjustmentData: builder.mutation({ query: createMutation("POST") }),
  }),
});

export const {
  useLazyGetAllDataQuery,
  useCreateDataMutation,
  useCalcEngineDataMutation,
  useDeleteDataRecordMutation,
  useDeleteDataMutation,
  useUpdateDataMutation,
  useChemicalAdjustmentDataMutation,
} = postApi;
